import type {
  AdminCentral,
  AdminCentralAdmin,
  AdminCentralPayload,
  AdminDoctor,
  AdminDoctorDetail,
  AdminDoctorFilters,
  AdminDoctorPayload,
  AdminDoctorResetPasswordPayload,
  AdminDoctorJwtSession,
  AdminForm,
  AdminFormPayload,
  AdminReviewer,
  AdminReviewerDetail,
  AdminReviewerPayload,
  AdminWitness,
  AdminWitnessPayload,
  AdminCountry,
  AdminCountryDetail,
  AdminCountryPayload,
  AdminCity,
  AdminCityPayload,
  AdminSpecialization,
  AdminSpecializationPayload,
  AdminSpecForm,
  AdminSpecFormPayload,
  AdminMajor,
  Central,
  DoctorCompleteness,
} from "@/types/admin";
import type { ListParams, Paginated } from "../../api";
import { normalizePaginated, unwrapItem } from "../../apiNormalize";
import { buildAdminCentralsQuery, centralFormToApi, mapApiAdminCentral, mapApiCentral } from "../../adminApi";
import { buildFilterQuery, del, get, post, put } from "../helpers";

// ——— Centrals ———

export async function fetchCentrals(params?: ListParams): Promise<Paginated<AdminCentral>> {
  const body = await get<unknown>(`/admin/centrals${buildAdminCentralsQuery(params)}`);
  const page = normalizePaginated<Record<string, unknown>>(body);
  return { ...page, data: page.data.map(mapApiAdminCentral) };
}

export async function fetchAdminCentral(id: number): Promise<Central> {
  const body = await get<unknown>(`/admin/centrals/${id}`);
  return mapApiCentral(unwrapItem<Record<string, unknown>>(body));
}

export async function fetchAdminCentralRaw(id: number): Promise<AdminCentral> {
  const body = await get<unknown>(`/admin/centrals/${id}?include=admins`);
  return mapApiAdminCentral(unwrapItem<Record<string, unknown>>(body));
}

export async function createCentral(payload: AdminCentralPayload): Promise<AdminCentral> {
  const body = await post<unknown>("/admin/centrals", centralFormToApi(payload));
  return mapApiAdminCentral(unwrapItem<Record<string, unknown>>(body));
}

export async function updateCentral(id: number, payload: AdminCentralPayload): Promise<AdminCentral> {
  const body = await put<unknown>(`/admin/centrals/${id}`, centralFormToApi(payload));
  return mapApiAdminCentral(unwrapItem<Record<string, unknown>>(body));
}

export async function deleteCentral(id: number): Promise<void> {
  await del(`/admin/centrals/${id}`);
}

export async function createAdminCentralAdmin(
  centralId: number,
  payload: { name: string; email: string; mobile?: string; password?: string; status?: number; role?: string },
): Promise<AdminCentralAdmin> {
  const body = await post<unknown>(`/admin/centrals/${centralId}/admins`, payload);
  return unwrapItem<AdminCentralAdmin>(body);
}

export async function updateAdminCentralAdmin(
  centralId: number,
  adminId: number,
  payload: { name: string; email: string; mobile?: string; password?: string; status?: number; role?: string },
): Promise<AdminCentralAdmin> {
  const body = await put<unknown>(`/admin/centrals/${centralId}/admins/${adminId}`, payload);
  return unwrapItem<AdminCentralAdmin>(body);
}

// ——— Doctors ———

export async function fetchAdminDoctors(
  params?: ListParams,
): Promise<Paginated<AdminDoctor> & { filters?: AdminDoctorFilters; totals?: { active: number; inactive: number } }> {
  const body = await get<Record<string, unknown>>(`/admin/doctors${buildFilterQuery(params)}`);
  const page = normalizePaginated<AdminDoctor>(body);
  const meta = (body.meta ?? {}) as Record<string, unknown>;
  return {
    ...page,
    filters: meta.filters as AdminDoctorFilters | undefined,
    totals: {
      active: Number(meta.total_active ?? 0),
      inactive: Number(meta.total_inactive ?? 0),
    },
  };
}

export async function fetchAdminDoctor(id: number): Promise<AdminDoctorDetail> {
  const body = await get<unknown>(`/admin/doctors/${id}`);
  return unwrapItem<AdminDoctorDetail>(body);
}

export async function createAdminDoctor(payload: AdminDoctorPayload): Promise<AdminDoctor> {
  const body = await post<unknown>("/admin/doctors", payload);
  return unwrapItem<AdminDoctor>(body);
}

export async function updateAdminDoctor(id: number, payload: Partial<AdminDoctorPayload>): Promise<AdminDoctor> {
  const body = await put<unknown>(`/admin/doctors/${id}`, payload);
  return unwrapItem<AdminDoctor>(body);
}

export async function deleteAdminDoctor(id: number): Promise<void> {
  await del(`/admin/doctors/${id}`);
}

export async function restoreAdminDoctor(id: number): Promise<AdminDoctor> {
  const body = await post<unknown>(`/admin/doctors/${id}/restore`, {});
  return unwrapItem<AdminDoctor>(body);
}

/** Result of a bulk action — ids that succeeded vs. failed (§B14). */
export interface BulkActionResult {
  ok: (number | string)[];
  failed: (number | string)[];
}

/**
 * Bulk action over selected doctors (§B14): `POST /admin/doctors/bulk` with
 * `{ action, ids }`. Returns `{ ok, failed }`. The endpoint may not be live in
 * every environment yet — callers should catch a 404 and degrade gracefully.
 */
export async function bulkAdminDoctors(
  action: string,
  ids: (number | string)[],
): Promise<BulkActionResult> {
  const body = await post<Partial<BulkActionResult>>("/admin/doctors/bulk", { action, ids });
  return { ok: body.ok ?? [], failed: body.failed ?? [] };
}

// ——— Doctor ops ———

export async function resetAdminDoctorPassword(id: number, payload: AdminDoctorResetPasswordPayload): Promise<void> {
  await post(`/admin/doctors/${id}/reset-password`, payload);
}

export async function fetchAdminDoctorJwtSessions(id: number): Promise<AdminDoctorJwtSession[]> {
  const body = await get<{ data?: AdminDoctorJwtSession[] }>(`/admin/doctors/${id}/sessions`);
  return body.data ?? [];
}

export async function revokeAdminDoctorJwtSession(doctorId: number, tokenId: string | number): Promise<void> {
  await del(`/admin/doctors/${doctorId}/sessions/${tokenId}`);
}

export async function revokeAllAdminDoctorJwtSessions(doctorId: number): Promise<void> {
  await del(`/admin/doctors/${doctorId}/sessions`);
}

export async function nudgeAdminDoctorSms(id: number): Promise<void> {
  await post(`/admin/doctors/${id}/nudge-sms`, {});
}

// ——— Reviewers ———

export async function fetchAdminReviewer(id: number): Promise<AdminReviewerDetail> {
  const body = await get<unknown>(`/admin/reviewers/${id}`);
  return unwrapItem<AdminReviewerDetail>(body);
}

export async function fetchAdminReviewers(params?: ListParams): Promise<Paginated<AdminReviewer>> {
  const body = await get<unknown>(`/admin/reviewers${buildFilterQuery(params)}`);
  return normalizePaginated<AdminReviewer>(body);
}

export async function createAdminReviewer(payload: AdminReviewerPayload): Promise<AdminReviewer> {
  const body = await post<unknown>("/admin/reviewers", payload);
  return unwrapItem<AdminReviewer>(body);
}

export async function updateAdminReviewer(id: number, payload: Partial<AdminReviewerPayload>): Promise<AdminReviewer> {
  const body = await put<unknown>(`/admin/reviewers/${id}`, payload);
  return unwrapItem<AdminReviewer>(body);
}

export async function deleteAdminReviewer(id: number): Promise<void> {
  await del(`/admin/reviewers/${id}`);
}

export async function restoreAdminReviewer(id: number): Promise<AdminReviewer> {
  const body = await post<unknown>(`/admin/reviewers/${id}/restore`, {});
  return unwrapItem<AdminReviewer>(body);
}

// ——— Witnesses ———

export async function fetchAdminWitnesses(params?: ListParams): Promise<Paginated<AdminWitness>> {
  const body = await get<unknown>(`/admin/witnesses${buildFilterQuery(params)}`);
  return normalizePaginated<AdminWitness>(body);
}

export async function createAdminWitness(payload: AdminWitnessPayload): Promise<AdminWitness> {
  const body = await post<unknown>("/admin/witnesses", payload);
  return unwrapItem<AdminWitness>(body);
}

export async function updateAdminWitness(
  id: number,
  payload: Partial<AdminWitnessPayload>,
): Promise<AdminWitness> {
  const body = await put<unknown>(`/admin/witnesses/${id}`, payload);
  return unwrapItem<AdminWitness>(body);
}

export async function deleteAdminWitness(id: number): Promise<void> {
  await del(`/admin/witnesses/${id}`);
}

// ——— Specializations ———

export async function fetchAdminSpecializations(params?: ListParams): Promise<Paginated<AdminSpecialization>> {
  const body = await get<unknown>(`/admin/specializations${buildFilterQuery(params)}`);
  return normalizePaginated<AdminSpecialization>(body);
}

export async function createAdminSpecialization(payload: AdminSpecializationPayload): Promise<AdminSpecialization> {
  const body = await post<unknown>("/admin/specializations", payload);
  return unwrapItem<AdminSpecialization>(body);
}

export async function updateAdminSpecialization(
  id: number,
  payload: Partial<AdminSpecializationPayload>,
): Promise<AdminSpecialization> {
  const body = await put<unknown>(`/admin/specializations/${id}`, payload);
  return unwrapItem<AdminSpecialization>(body);
}

export async function deleteAdminSpecialization(id: number): Promise<void> {
  await del(`/admin/specializations/${id}`);
}

export async function restoreAdminSpecialization(id: number): Promise<AdminSpecialization> {
  const body = await post<unknown>(`/admin/specializations/${id}/restore`, {});
  return unwrapItem<AdminSpecialization>(body);
}

export async function fetchAdminMajors(): Promise<AdminMajor[]> {
  const body = await get<{ data?: AdminMajor[] }>("/admin/majors");
  const list = body.data ?? [];
  return list.map((m) => ({ ...m, name: m.name ?? m.label }));
}

// ——— Specialization forms (forms-linked spec catalog) ———

export async function fetchAdminSpecForms(params?: ListParams): Promise<Paginated<AdminSpecForm>> {
  const body = await get<unknown>(`/admin/specialization-forms${buildFilterQuery(params)}`);
  return normalizePaginated<AdminSpecForm>(body);
}

export async function fetchAdminSpecForm(id: number): Promise<AdminSpecForm> {
  const body = await get<unknown>(`/admin/specialization-forms/${id}`);
  return unwrapItem<AdminSpecForm>(body);
}

export async function createAdminSpecForm(payload: AdminSpecFormPayload): Promise<AdminSpecForm> {
  const body = await post<unknown>("/admin/specialization-forms", payload);
  return unwrapItem<AdminSpecForm>(body);
}

export async function updateAdminSpecForm(
  id: number,
  payload: Partial<AdminSpecFormPayload>,
): Promise<AdminSpecForm> {
  const body = await put<unknown>(`/admin/specialization-forms/${id}`, payload);
  return unwrapItem<AdminSpecForm>(body);
}

export async function deleteAdminSpecForm(id: number): Promise<void> {
  await del(`/admin/specialization-forms/${id}`);
}

export async function restoreAdminSpecForm(id: number): Promise<AdminSpecForm> {
  const body = await post<unknown>(`/admin/specialization-forms/${id}/restore`, {});
  return unwrapItem<AdminSpecForm>(body);
}

// ——— Forms (consent templates, titles in language_details) ———

export async function fetchAdminForms(params?: ListParams): Promise<Paginated<AdminForm>> {
  const body = await get<unknown>(`/admin/forms${buildFilterQuery(params)}`);
  return normalizePaginated<AdminForm>(body);
}

export async function fetchAdminForm(id: number): Promise<AdminForm> {
  const body = await get<unknown>(`/admin/forms/${id}`);
  return unwrapItem<AdminForm>(body);
}

export async function createAdminForm(payload: AdminFormPayload): Promise<AdminForm> {
  const body = await post<unknown>("/admin/forms", payload);
  return unwrapItem<AdminForm>(body);
}

export async function updateAdminForm(
  id: number,
  payload: Partial<AdminFormPayload>,
): Promise<AdminForm> {
  const body = await put<unknown>(`/admin/forms/${id}`, payload);
  return unwrapItem<AdminForm>(body);
}

export async function deleteAdminForm(id: number): Promise<void> {
  await del(`/admin/forms/${id}`);
}

export async function restoreAdminForm(id: number): Promise<AdminForm> {
  const body = await post<unknown>(`/admin/forms/${id}/restore`, {});
  return unwrapItem<AdminForm>(body);
}

// ——— Countries & Cities ———

export async function fetchAdminCountries(params?: ListParams): Promise<Paginated<AdminCountry>> {
  const body = await get<unknown>(`/admin/countries${buildFilterQuery(params)}`);
  return normalizePaginated<AdminCountry>(body);
}

export async function fetchAdminCountry(id: number): Promise<AdminCountryDetail> {
  const body = await get<unknown>(`/admin/countries/${id}`);
  return unwrapItem<AdminCountryDetail>(body);
}

export async function createAdminCountry(payload: AdminCountryPayload): Promise<AdminCountry> {
  const body = await post<unknown>("/admin/countries", payload);
  return unwrapItem<AdminCountry>(body);
}

export async function updateAdminCountry(
  id: number,
  payload: Partial<AdminCountryPayload>,
): Promise<AdminCountry> {
  const body = await put<unknown>(`/admin/countries/${id}`, payload);
  return unwrapItem<AdminCountry>(body);
}

export async function deleteAdminCountry(id: number): Promise<void> {
  await del(`/admin/countries/${id}`);
}

// ——— Admin Reference Data ———

export interface AdminReferenceCountry {
  id: number;
  name: string;
  call_code: string;
  iso_code: string;
  
}

export interface AdminReferenceData {
  countries: AdminReferenceCountry[];
}

export async function fetchAdminReference(): Promise<AdminReferenceData> {
  const body = await get<unknown>("/admin/reference");
  return unwrapItem<AdminReferenceData>(body);
}

export async function fetchAdminCities(countryId: number, params?: ListParams): Promise<Paginated<AdminCity>> {
  const body = await get<unknown>(`/admin/countries/${countryId}/cities${buildFilterQuery(params)}`);
  return normalizePaginated<AdminCity>(body);
}

export async function createAdminCity(countryId: number, payload: AdminCityPayload): Promise<AdminCity> {
  const body = await post<unknown>(`/admin/countries/${countryId}/cities`, payload);
  return unwrapItem<AdminCity>(body);
}

export async function updateAdminCity(
  countryId: number,
  cityId: number,
  payload: Partial<AdminCityPayload>,
): Promise<AdminCity> {
  const body = await put<unknown>(`/admin/countries/${countryId}/cities/${cityId}`, payload);
  return unwrapItem<AdminCity>(body);
}

export async function deleteAdminCity(countryId: number, cityId: number): Promise<void> {
  await del(`/admin/countries/${countryId}/cities/${cityId}`);
}

// ——— Doctor completeness monitor ———

export async function fetchDoctorCompleteness(params?: ListParams): Promise<Paginated<DoctorCompleteness>> {
  const body = await get<Record<string, unknown>>(`/admin/doctors${buildFilterQuery(params)}`);
  const page = normalizePaginated<Record<string, unknown>>(body);
  const data = page.data.map((d): DoctorCompleteness => {
    const rec = d as Record<string, unknown>;
    const central = rec.central as { name?: string; license_number?: string | null } | null | undefined;
    const fields: { key: DoctorCompleteness["missing"][number]; filled: boolean }[] = [
      { key: "mobile", filled: Boolean(rec.mobile) },
      { key: "major", filled: Boolean(rec.major) },
      { key: "specialization", filled: Array.isArray(rec.specializations) && rec.specializations.length > 0 },
      { key: "medical_pro_class_number", filled: Boolean(rec.medical_pro_class_number) },
      { key: "medical_center_license_number", filled: Boolean(central?.license_number) },
    ];
    const missing = fields.filter((f) => !f.filled).map((f) => f.key);
    const filled = fields.length - missing.length;
    const pct = filled / fields.length;
    return {
      id: Number(rec.id),
      name: String(rec.name ?? ""),
      email: String(rec.email ?? ""),
      mobile: String(rec.mobile ?? ""),
      central_name: central?.name ?? null,
      filled,
      total: fields.length,
      missing,
      level: pct >= 1 ? "green" : pct >= 0.6 ? "yellow" : "red",
      last_nudge_at: (rec.last_nudge_at as string | null) ?? null,
    };
  });
  return { ...page, data };
}
